import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import api from "../lib/api";
import { Sparkles, Bot, Users2, BookOpenCheck, Trophy, Clock, ArrowRight, Shield, Check, Mail, Phone } from "lucide-react";

export default function Landing() {
  return (
    <div data-testid="landing-page" className="relative overflow-hidden">
      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20">
        <div className="blob -top-32 -left-32 w-96 h-96 bg-sky-200" />
        <div className="blob top-20 -right-24 w-[28rem] h-[28rem] bg-amber-200" />
        <div className="blob bottom-0 left-1/3 w-80 h-80 bg-rose-200" />

        <div className="relative grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 animate-pop">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm mb-6">
              <Sparkles size={14} className="text-amber-500" strokeWidth={3} />
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-slate-700">The Haddis Academy LLC · Virginia SOL · K through 5</span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight text-slate-900 leading-[1.05]">
              Where curious kids meet
              <span className="bg-gradient-to-r from-sky-500 to-amber-500 bg-clip-text text-transparent"> brilliant teachers</span> &amp; smart AI.
            </h1>
            <p className="mt-7 text-lg lg:text-xl text-slate-600 max-w-xl leading-relaxed">
              A 50/50 blend of a personal AI tutor and a real, certified live teacher.
              Three hours a day, five days a week. Built around the Virginia Standards of Learning,
              so your kid doesn't just learn — they thrive.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/auth?mode=signup" data-testid="hero-get-started">
                <Button className="rounded-full bg-sky-400 hover:bg-sky-500 text-white font-bold text-base px-8 py-6 shadow-xl shadow-sky-200 hover:-translate-y-0.5 transition">
                  Start free <ArrowRight className="ml-2" size={18} strokeWidth={3}/>
                </Button>
              </Link>
              <a href="#pricing" data-testid="hero-pricing">
                <Button variant="outline" className="rounded-full border-2 border-slate-200 bg-white hover:bg-slate-50 font-bold text-base px-8 py-6">
                  See pricing
                </Button>
              </a>
              <Link to="/auth?mode=login" data-testid="hero-login">
                <Button variant="ghost" className="rounded-full hover:bg-slate-100 font-bold text-base px-6 py-6">
                  Log in
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-8 text-slate-500 text-sm">
              <div className="flex items-center gap-2"><Shield size={16} className="text-emerald-500" strokeWidth={3}/> COPPA-aware</div>
              <div className="flex items-center gap-2"><Clock size={16} className="text-sky-500" strokeWidth={3}/> 15 hrs / week</div>
              <div className="flex items-center gap-2"><Trophy size={16} className="text-amber-500" strokeWidth={3}/> Quiz at each lesson</div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="grid grid-cols-6 gap-4 animate-pop">
              <div className="col-span-4 row-span-2 bg-white rounded-3xl p-2 shadow-[0_20px_50px_rgba(2,132,199,0.18)] border border-slate-100">
                <img
                  src="https://images.unsplash.com/photo-1741279426915-8826b7bc342b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHw0fHxjaGlsZCUyMHN0dWR5aW5nJTIwbGFwdG9wJTIwaGFwcHl8ZW58MHx8fHwxNzc3MzM2Nzk0fDA&ixlib=rb-4.1.0&q=85"
                  alt="happy child learning"
                  className="rounded-2xl object-cover h-72 lg:h-96 w-full"
                />
              </div>
              <div className="col-span-2 bg-amber-300 rounded-3xl p-6 flex flex-col justify-between min-h-[10rem] animate-float">
                <Bot strokeWidth={2.5} size={28} className="text-slate-900"/>
                <div>
                  <div className="text-3xl font-black text-slate-900">7.5h</div>
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-700/80">AI tutoring / wk</div>
                </div>
              </div>
              <div className="col-span-2 bg-rose-300 rounded-3xl p-6 flex flex-col justify-between min-h-[10rem]">
                <Users2 strokeWidth={2.5} size={28} className="text-slate-900"/>
                <div>
                  <div className="text-3xl font-black text-slate-900">7.5h</div>
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-700/80">Live teacher / wk</div>
                </div>
              </div>
              <div className="col-span-6 bg-slate-900 text-white rounded-3xl p-6 flex items-center gap-4">
                <BookOpenCheck className="text-amber-300" size={26} strokeWidth={2.5}/>
                <div>
                  <div className="text-sm font-bold">120+ lessons mapped to Virginia SOL codes</div>
                  <div className="text-xs text-slate-400 mt-1">Math · Reading · Science · Social Studies</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature row */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Bot, color: "sky", title: "AI Tutor that listens", body: "GPT-5.2 powered tutor adapts to your child's grade and pace, in real time." },
            { icon: Users2, color: "rose", title: "Real Certified Teachers", body: "Book live small-group sessions with Virginia-credentialed educators." },
            { icon: Trophy, color: "amber", title: "Engaging activities & quizzes", body: "Every lesson ends with a fun activity and a 5-question quiz to lock it in." },
          ].map((f, i) => (
            <div key={i} data-testid={`feature-card-${i}`} className="bg-white rounded-3xl p-8 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-xl transition-all">
              <div className={`w-14 h-14 rounded-2xl bg-${f.color}-100 flex items-center justify-center mb-5`}>
                <f.icon className={`text-${f.color}-600`} strokeWidth={2.5}/>
              </div>
              <h3 className="text-2xl font-black text-slate-900 mb-2">{f.title}</h3>
              <p className="text-slate-600 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <PricingSection />

      <footer className="border-t border-slate-100 py-10 text-center text-sm text-slate-500">
        <div className="font-bold text-slate-700 mb-1">The Haddis Academy LLC</div>
        <div>newagelearning@thehaddisacademy.com · (571) 466-7348</div>
        <div className="mt-2">© {new Date().getFullYear()} · Aligned with Virginia Standards of Learning</div>
      </footer>
    </div>
  );
}

function PricingSection() {
  const [form, setForm] = useState({ parent_name: "", email: "", phone: "", grade: "K", note: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enroll", form);
      setDone(true);
      toast.success("Thanks! Ms. Haddis will reach out shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please email or call directly.");
    } finally {
      setBusy(false);
    }
  };

  const included = [
    "15 hours of learning per week (3h × 5 days)",
    "7.5h with our AI tutor + 7.5h live with a VA-certified teacher",
    "All 4 core subjects: Math, English, Science, Social Studies",
    "Virginia SOL aligned · Grades K through 5",
    "Objectives, fun activity & summary quiz in every lesson",
    "Progress reports for parents",
  ];

  return (
    <section data-testid="pricing-section" id="pricing" className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 border border-amber-200 mb-5">
          <Sparkles size={14} className="text-amber-700" strokeWidth={3}/>
          <span className="text-xs font-black uppercase tracking-[0.2em] text-amber-800">Simple, honest pricing</span>
        </div>
        <h2 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight">
          One plan. Everything included.
        </h2>
        <p className="text-slate-600 text-lg mt-4">No tiers, no upsells. Your child gets the full Haddis Academy experience.</p>
      </div>

      <div className="grid lg:grid-cols-12 gap-6 items-start">
        {/* Price card */}
        <div className="lg:col-span-5 bg-slate-900 text-white rounded-3xl p-8 lg:p-10 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-56 h-56 bg-sky-500/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-16 -left-16 w-56 h-56 bg-amber-400/20 rounded-full blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-300 text-slate-900 mb-6">
              <span className="text-[10px] font-black uppercase tracking-widest">7-day free trial</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-7xl font-black tracking-tight">$179</span>
              <span className="text-slate-300 font-bold">/ month</span>
            </div>
            <div className="text-sm text-slate-400 mt-1">per child · cancel anytime</div>

            <ul className="mt-8 space-y-3">
              {included.map((line, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-400/20 text-emerald-300 flex items-center justify-center shrink-0">
                    <Check size={12} strokeWidth={3}/>
                  </span>
                  <span className="text-slate-100">{line}</span>
                </li>
              ))}
            </ul>

            <Link to="/auth?mode=signup" data-testid="pricing-start-trial"
                  className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-amber-300 hover:bg-amber-400 text-slate-900 font-black px-6 py-4 transition">
              Start 7-day free trial <ArrowRight size={16} strokeWidth={3}/>
            </Link>

            <div className="mt-6 pt-6 border-t border-slate-700 grid grid-cols-2 gap-3">
              <a href="mailto:newagelearning@thehaddisacademy.com" data-testid="contact-email"
                 className="flex items-center justify-center gap-2 rounded-full bg-white/10 hover:bg-white/20 px-4 py-3 text-sm font-bold transition">
                <Mail size={14} strokeWidth={2.5}/> Email
              </a>
              <a href="tel:+15714667348" data-testid="contact-phone"
                 className="flex items-center justify-center gap-2 rounded-full bg-white/10 hover:bg-white/20 px-4 py-3 text-sm font-bold transition">
                <Phone size={14} strokeWidth={2.5}/> Call
              </a>
            </div>
            <div className="mt-3 text-center text-xs text-slate-400 break-all">
              newagelearning@thehaddisacademy.com · (571) 466-7348
            </div>
          </div>
        </div>

        {/* Enrollment form */}
        <div className="lg:col-span-7 bg-white rounded-3xl p-8 lg:p-10 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          {done ? (
            <div data-testid="enroll-success" className="text-center py-10">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-100 flex items-center justify-center mb-4">
                <Check className="text-emerald-600" strokeWidth={3} />
              </div>
              <h3 className="text-2xl font-black text-slate-900">You're on the list!</h3>
              <p className="text-slate-600 mt-2 max-w-md mx-auto">
                Thanks for your interest in The Haddis Academy. Ms. Haddis will reach out within 24 hours
                to set up your child's first free lesson.
              </p>
            </div>
          ) : (
            <>
              <h3 className="text-2xl font-black text-slate-900 mb-1">Request enrollment</h3>
              <p className="text-slate-500 text-sm mb-6">Fill this out and Ms. Haddis will reach out personally.</p>
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Parent name</Label>
                  <Input data-testid="enroll-parent" required value={form.parent_name}
                    onChange={(e)=>setForm({...form, parent_name: e.target.value})}
                    className="mt-2 rounded-xl h-12 border-slate-200" placeholder="Jane Doe"/>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Email</Label>
                  <Input data-testid="enroll-email" type="email" required value={form.email}
                    onChange={(e)=>setForm({...form, email: e.target.value})}
                    className="mt-2 rounded-xl h-12 border-slate-200" placeholder="parent@email.com"/>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Phone</Label>
                  <Input data-testid="enroll-phone" required value={form.phone}
                    onChange={(e)=>setForm({...form, phone: e.target.value})}
                    className="mt-2 rounded-xl h-12 border-slate-200" placeholder="(555) 555-1234"/>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Grade</Label>
                  <Select value={form.grade} onValueChange={(v)=>setForm({...form, grade: v})}>
                    <SelectTrigger data-testid="enroll-grade" className="mt-2 rounded-xl h-12 border-slate-200">
                      <SelectValue/>
                    </SelectTrigger>
                    <SelectContent>
                      {["K","1","2","3","4","5"].map(g => (
                        <SelectItem key={g} value={g}>{g === "K" ? "Kindergarten" : `Grade ${g}`}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Start date</Label>
                  <Input data-testid="enroll-start" placeholder="ASAP / Aug 2026 / etc."
                    onChange={(e)=>setForm({...form, note: e.target.value})}
                    className="mt-2 rounded-xl h-12 border-slate-200"/>
                </div>
                <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-3 mt-2">
                  <Button data-testid="enroll-submit" type="submit" disabled={busy}
                    className="rounded-full bg-sky-400 hover:bg-sky-500 text-white font-bold h-12 px-8 shadow-lg shadow-sky-200">
                    {busy ? "Sending…" : "Request first free lesson"}
                  </Button>
                  <span className="text-xs text-slate-500">No card needed · We'll call within 24 hrs.</span>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
